import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { supabase } from '../services/supabaseService';

const ResetPasswordPage: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
  const [isValidToken, setIsValidToken] = useState(true);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  useEffect(() => {
    checkTokenValidity();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = '/';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [success]);

  const checkTokenValidity = async () => {
    // In PKCE the Supabase client exchanges ?code= for a session asynchronously
    // after mount, and getSession() returns null until it completes. Give the
    // client up to ~3s to establish the recovery session, and also accept the
    // PASSWORD_RECOVERY event as a positive signal.
    let resolved = false;
    const authSub = supabase.auth.onAuthStateChange((event, session) => {
      if (resolved) return;
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        resolved = true;
        authSub.data.subscription?.unsubscribe();
      }
    });

    try {
      for (let i = 0; i < 6; i++) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          resolved = true;
          authSub.data.subscription?.unsubscribe();
          return;
        }
        await new Promise((r) => setTimeout(r, 500));
      }

      authSub.data.subscription?.unsubscribe();
      if (!resolved) {
        setIsValidToken(false);
        setError('El enlace de recuperación es inválido o ha expirado. Por favor, solicita un nuevo enlace.');
      }
    } catch (error) {
      authSub.data.subscription?.unsubscribe();
      console.error('Error checking token:', error);
      setIsValidToken(false);
      setError('Error validando el enlace de recuperación.');
    }
  };

  const calculatePasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    let label = '';
    let color = '';
    if (score <= 2) { label = 'Débil'; color = 'bg-red-500'; }
    else if (score <= 4) { label = 'Media'; color = 'bg-yellow-500'; }
    else { label = 'Fuerte'; color = 'bg-green-500'; }

    setPasswordStrength({ score, label, color });
  };

  const validatePassword = (password: string): { valid: boolean; error?: string } => {
    if (password.length < 8) {
      return { valid: false, error: 'La contraseña debe tener al menos 8 caracteres' };
    }
    if (!/[A-Z]/.test(password)) {
      return { valid: false, error: 'La contraseña debe contener al menos una mayúscula' };
    }
    if (!/[a-z]/.test(password)) {
      return { valid: false, error: 'La contraseña debe contener al menos una minúscula' };
    }
    if (!/[0-9]/.test(password)) {
      return { valid: false, error: 'La contraseña debe contener al menos un número' };
    }
    return { valid: true };
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error || 'Contraseña inválida');
      return;
    }

    setIsLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        if (updateError.message.includes('expired') || updateError.message.includes('invalid')) {
          setError('El enlace ha expirado. Por favor, solicita un nuevo enlace de recuperación.');
        } else {
          setError(`Error al actualizar la contraseña: ${updateError.message}`);
        }
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          await sendPasswordChangedEmail(user.email);
        }
      } catch (emailError) {
        console.warn('No se pudo enviar email de confirmación:', emailError);
      }

      setSuccess(true);
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Error al cambiar la contraseña. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const sendPasswordChangedEmail = async (email: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      await supabase.functions.invoke('send-password-changed-email', {
        body: { email },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
    } catch (error) {
      console.error('Error sending confirmation email:', error);
    }
  };

  const handleBackToLogin = () => {
    window.location.href = '/';
  };

  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <img
              src="/logo-ceroideas.png"
              alt="Cero Ideas Logo"
              className="w-16 h-16 mx-auto mb-4 rounded-xl shadow-sm"
            />
            <h1 className="text-2xl font-bold text-gray-900">Enlace Expirado</h1>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 text-sm font-medium mb-1">Enlace Inválido o Expirado</p>
                <p className="text-red-600 text-sm">
                  El enlace de recuperación es válido solo por 5 minutos. Por favor, solicita un nuevo enlace.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleBackToLogin}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Volver al Login</span>
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          <div className="text-center">
            <img
              src="/logo-ceroideas.png"
              alt="Cero Ideas Logo"
              className="w-16 h-16 mx-auto mb-4 rounded-xl shadow-sm"
            />

            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">Contraseña Actualizada</h1>
            <p className="text-gray-600 mb-6">
              Tu contraseña ha sido cambiada correctamente. Se ha enviado un email de confirmación.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-700">
                Serás redirigido al login en <strong>{redirectCountdown}</strong> segundos...
              </p>
            </div>

            <button
              onClick={handleBackToLogin}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Ir al Login Ahora
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <div className="text-center mb-8">
          <img
            src="/logo-ceroideas.png"
            alt="Cero Ideas Logo"
            className="w-16 h-16 mx-auto mb-4 rounded-xl shadow-sm"
          />
          <h1 className="text-2xl font-bold text-gray-900">Nueva Contraseña</h1>
          <p className="text-gray-600 mt-2">Establece tu nueva contraseña</p>
        </div>

        <form onSubmit={handlePasswordChange} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              <strong>Importante:</strong> Este enlace expira en 5 minutos desde que fue enviado.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva contraseña *
              <span className="block text-xs text-gray-500 font-normal mt-1">
                Mínimo 8 caracteres, incluye mayúsculas, minúsculas y números
              </span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  calculatePasswordStrength(e.target.value);
                }}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {newPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Fortaleza:</span>
                  <span className={`text-xs font-medium ${
                    passwordStrength.score <= 2 ? 'text-red-600' :
                    passwordStrength.score <= 4 ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${passwordStrength.color}`}
                    style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar nueva contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="••••••••"
              />
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-red-600 mt-1">Las contraseñas no coinciden</p>
            )}
          </div>

          <div className="space-y-3 pt-4">
            <button
              type="submit"
              disabled={isLoading || !newPassword || newPassword !== confirmPassword}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Actualizando contraseña...
                </>
              ) : (
                'Actualizar Contraseña'
              )}
            </button>

            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Volver al Login</span>
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            © 2025 Cero Ideas. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
